import {
	Autocomplete,
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	IconButton,
	TextField,
	Typography,
} from '@mui/material';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import SnackbarContext from '../../store/snackbar-context';
import AuthContext from '../../store/auth-context';
import ConfirmationDialog from './ConfirmationDialog';
import { updateOlympicsTopics } from '../../api/content-api';

type TopicsEditorDialogProps = {
	open: boolean;
	type: 'oral' | 'linguistic';
	title: string;
	topics: any[] | null;
	specializationId?: number | null;
	specializationName?: string | null;
	onClose: () => void;
	onUpdated: (updated: any[], type: 'oral' | 'linguistic') => void;
};

type EditableTopic = {
	clientId: string;
	id?: number | null;
	topic: string;
	category: string;
};

const DEFAULT_LINGUISTIC_CATEGORY = 'JĘZYKOZNAWSTWO';

const TopicsEditorDialog = ({
	open,
	type,
	title,
	topics,
	specializationId,
	specializationName,
	onClose,
	onUpdated,
}: TopicsEditorDialogProps) => {
	const { setMsg } = useContext(SnackbarContext);
	const { token } = useContext(AuthContext);
	const [items, setItems] = useState<EditableTopic[]>([]);
	const [isDirty, setIsDirty] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [confirmCloseText, setConfirmCloseText] = useState<string | null>(null);
	const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
	const [autoSaveStatus, setAutoSaveStatus] = useState<string | null>(null);
	const autoSaveStatusTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	const isLinguistic = type === 'linguistic';

	const sortedTopics = useMemo(() => {
		const list = topics ? [...topics] : [];
		return list.sort((a: any, b: any) => {
			const orderDiff = (a.sort_order ?? 0) - (b.sort_order ?? 0);
			if (orderDiff !== 0) {
				return orderDiff;
			}
			return (a.id ?? 0) - (b.id ?? 0);
		});
	}, [topics]);

	const categoryOptions = useMemo(() => {
		if (isLinguistic) {
			return [];
		}
		const values = items.map((item) => item.category.trim()).filter((value) => value.length > 0);
		return Array.from(new Set(values));
	}, [items, isLinguistic]);

	useEffect(() => {
		if (!open || isDirty) {
			return;
		}
		const filtered = sortedTopics.filter((topic: any) => {
			const isTopicLinguistic = topic.is_linguistic == 1;
			if (isTopicLinguistic !== isLinguistic) {
				return false;
			}
			if (!isLinguistic && specializationId) {
				return (topic.specializations || []).some((spec: any) => spec.id === specializationId);
			}
			return true;
		});
		const mapped = filtered.map((topic: any) => ({
			clientId: `topic-${topic.id}`,
			id: topic.id,
			topic: topic.topic || '',
			category: topic.category || (isLinguistic ? DEFAULT_LINGUISTIC_CATEGORY : ''),
		}));
		setItems(mapped);
		setIsDirty(false);
		setAutoSaveStatus(null);
	}, [open, isDirty, sortedTopics, isLinguistic, specializationId]);

	useEffect(() => {
		if (!open) {
			setIsDirty(false);
			setAutoSaveStatus(null);
		}
	}, [open]);

	const isValidList = useCallback(
		(list: EditableTopic[]) => {
			return list.every((item) => {
				const hasTopic = item.topic.trim().length > 0;
				const hasCategory = isLinguistic || item.category.trim().length > 0;
				return hasTopic && hasCategory;
			});
		},
		[isLinguistic],
	);

	const updateItem = (clientId: string, changes: Partial<EditableTopic>) => {
		setItems((prev) => prev.map((item) => (item.clientId === clientId ? { ...item, ...changes } : item)));
		setIsDirty(true);
		setAutoSaveStatus(null);
	};

	const requestRowSave = (row: EditableTopic) => {
		if (!autoSaveEnabled || isSaving) {
			return;
		}
		const hasTopic = row.topic.trim().length > 0;
		const hasCategory = isLinguistic || row.category.trim().length > 0;
		if (hasTopic && hasCategory) {
			handleSave('auto');
		}
	};

	const moveItem = (index: number, direction: -1 | 1) => {
		setItems((prev) => {
			const nextIndex = index + direction;
			if (nextIndex < 0 || nextIndex >= prev.length) {
				return prev;
			}
			const copy = [...prev];
			const [removed] = copy.splice(index, 1);
			copy.splice(nextIndex, 0, removed);
			return copy;
		});
		setIsDirty(true);
	};

	const removeItem = (clientId: string) => {
		setItems((prev) => prev.filter((item) => item.clientId !== clientId));
		setIsDirty(true);
	};

	const addItem = () => {
		const newItem: EditableTopic = {
			clientId: `new-${Date.now()}-${Math.random().toString(16).slice(2)}`,
			id: null,
			topic: '',
			category: isLinguistic ? DEFAULT_LINGUISTIC_CATEGORY : '',
		};
		setItems((prev) => [...prev, newItem]);
		setIsDirty(true);
	};

	const handleSave = useCallback(
		async (source: 'auto' | 'manual') => {
			if (isSaving) {
				return;
			}
			if (!isLinguistic && !specializationId) {
				setMsg({ msg: 'Brak specjalizacji do zapisu tematów' });
				return;
			}
			if (!isValidList(items)) {
				setMsg({ msg: 'Uzupełnij wszystkie pola przed zapisem' });
				return;
			}
			setIsSaving(true);
			const payload = items.map((item) => ({
				id: item.id,
				topic: item.topic.trim(),
				category: isLinguistic ? DEFAULT_LINGUISTIC_CATEGORY : item.category.trim(),
			}));
			try {
				const response = await updateOlympicsTopics(token, {
					type,
					specializationId: specializationId || undefined,
					topics: payload,
				});
				if (response.status === 200) {
					const updated = response.data || [];
					const sortedUpdated = [...updated].sort((a: any, b: any) => {
						const orderDiff = (a.sort_order ?? 0) - (b.sort_order ?? 0);
						if (orderDiff !== 0) {
							return orderDiff;
						}
						return (a.id ?? 0) - (b.id ?? 0);
					});
					setItems((prev) => {
						if (prev.length !== sortedUpdated.length) {
							return sortedUpdated.map((topic: any) => ({
								clientId: `topic-${topic.id}`,
								id: topic.id,
								topic: topic.topic || '',
								category: topic.category || (isLinguistic ? DEFAULT_LINGUISTIC_CATEGORY : ''),
							}));
						}
						return prev.map((item, index) => ({
							...item,
							id: sortedUpdated[index]?.id ?? item.id,
							category: sortedUpdated[index]?.category || (isLinguistic ? DEFAULT_LINGUISTIC_CATEGORY : item.category),
						}));
					});
					setIsDirty(false);
					if (source === 'auto') {
						setAutoSaveStatus('Automatycznie zapisano');
						if (autoSaveStatusTimer.current) {
							clearTimeout(autoSaveStatusTimer.current);
						}
						autoSaveStatusTimer.current = setTimeout(() => {
							setAutoSaveStatus(null);
						}, 3000);
					}
					onUpdated(updated, type);
				} else {
					setMsg({ msg: response.data });
				}
			} catch (exception: any) {
				console.error(exception);
				if (exception.response?.status === 401 || exception.response?.status === 403) {
					setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
				} else {
					setMsg({ msg: 'Błąd serwera. Spróbuj za chwilę' });
				}
			} finally {
				setIsSaving(false);
			}
		},
		[isSaving, isValidList, items, isLinguistic, onUpdated, setMsg, token, type, specializationId],
	);

	useEffect(() => {
		if (!open) {
			if (autoSaveTimer.current) {
				clearTimeout(autoSaveTimer.current);
			}
			if (autoSaveStatusTimer.current) {
				clearTimeout(autoSaveStatusTimer.current);
			}
			return;
		}
		if (!autoSaveEnabled || !isDirty || isSaving) {
			return;
		}
		if (!isValidList(items)) {
			return;
		}
		if (autoSaveTimer.current) {
			clearTimeout(autoSaveTimer.current);
		}
		autoSaveTimer.current = setTimeout(() => {
			handleSave('auto');
		}, 1500);

		return () => {
			if (autoSaveTimer.current) {
				clearTimeout(autoSaveTimer.current);
			}
		};
	}, [autoSaveEnabled, handleSave, isDirty, isSaving, isValidList, items, open]);

	const handleCloseRequest = () => {
		if (isDirty || isSaving) {
			setConfirmCloseText('Masz niezapisane zmiany. Czy chcesz zamknąć okno bez zapisu?');
			return;
		}
		onClose();
	};

	const confirmClose = () => {
		setConfirmCloseText(null);
		onClose();
	};

	return (
		<>
			<Dialog
				open={open}
				fullWidth
				maxWidth='md'
				onClose={handleCloseRequest}
				PaperProps={{ sx: { minWidth: '820px' } }}
			>
				<DialogTitle>
					{title}
					{specializationName ? ` — ${specializationName}` : ''}
				</DialogTitle>
				<DialogContent>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
						{items.length === 0 && <Typography variant='body2'>Brak tematów na liście.</Typography>}
						{items.map((item, index) => {
							const topicError = item.topic.trim().length === 0;
							const categoryError = !isLinguistic && item.category.trim().length === 0;
							return (
								<Box
									key={item.clientId}
									sx={{
										display: 'grid',
										gridTemplateColumns: isLinguistic ? '1fr auto auto auto' : '1fr 1fr auto auto auto',
										gap: 1,
										alignItems: 'center',
									}}
								>
									<TextField
										label='Temat'
										value={item.topic}
										onChange={(event) => updateItem(item.clientId, { topic: event.target.value })}
										onBlur={() => requestRowSave(item)}
										error={topicError}
										helperText={topicError ? 'Wpisz treść tematu' : ' '}
										size='small'
									/>
									{!isLinguistic && (
										<Autocomplete
											freeSolo
											options={categoryOptions}
											value={item.category}
											onChange={(_event, newValue) => {
												updateItem(item.clientId, { category: newValue ? String(newValue) : '' });
											}}
											onInputChange={(_event, newInputValue, reason) => {
												if (reason === 'input') {
													updateItem(item.clientId, { category: newInputValue });
												}
											}}
											renderInput={(params) => (
												<TextField
													{...params}
													label='Dział'
													error={categoryError}
													helperText={categoryError ? 'Wpisz nazwę działu' : ' '}
													size='small'
													onBlur={() => requestRowSave(item)}
												/>
											)}
										/>
									)}
									<IconButton
										aria-label='Przenieś wyżej'
										onClick={() => moveItem(index, -1)}
										disabled={index === 0}
									>
										<ArrowUpwardIcon fontSize='small' />
									</IconButton>
									<IconButton
										aria-label='Przenieś niżej'
										onClick={() => moveItem(index, 1)}
										disabled={index === items.length - 1}
									>
										<ArrowDownwardIcon fontSize='small' />
									</IconButton>
									<IconButton
										aria-label='Usuń'
										onClick={() => removeItem(item.clientId)}
									>
										<DeleteOutlineIcon fontSize='small' />
									</IconButton>
								</Box>
							);
						})}
					</Box>
				</DialogContent>
				<DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
						<Button
							startIcon={<AddIcon />}
							variant='outlined'
							onClick={addItem}
						>
							Dodaj temat
						</Button>
						<FormControlLabel
							control={
								<Checkbox
									checked={autoSaveEnabled}
									onChange={(event) => setAutoSaveEnabled(event.target.checked)}
								/>
							}
							label='Autozapis'
						/>
					</Box>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
						{autoSaveStatus && <Typography variant='caption'>{autoSaveStatus}</Typography>}
						{isDirty && <Typography variant='caption'>Niezapisane zmiany</Typography>}
						<Button
							variant='outlined'
							onClick={handleCloseRequest}
						>
							Zamknij
						</Button>
						<Button
							startIcon={<SaveIcon />}
							variant='contained'
							onClick={() => handleSave('manual')}
							disabled={!isDirty || !isValidList(items) || isSaving}
							sx={{ minWidth: '150px' }}
						>
							{isSaving ? 'Zapisywanie...' : 'Zapisz'}
						</Button>
					</Box>
				</DialogActions>
			</Dialog>
			<ConfirmationDialog
				close={() => setConfirmCloseText(null)}
				inProgress={false}
				text={confirmCloseText}
				onSuccess={confirmClose}
			/>
		</>
	);
};

export default TopicsEditorDialog;
